// FILE: src/pages/admin/ops/bookings/OpsBookings.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import OpsRightDrawer from "./components/OpsRightDrawer";
import OpsActionModal from "./components/OpsActionModal";
import {
  opsGetReasons,
  opsListBookings,
  opsGetBookingDetail,
  opsAddBookingNote,
  opsAuditLogs,
} from "../../../api/adminOpsApi";

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * Ride Now bookings should reflect the real live flow:
 * - ACTION_NEEDED: accepted bookings needing ops attention
 * - PICKUP_PASSED: pickup time passed but trip not started
 * - ACTIVE: all active bookings
 * - UNFULFILLED / COMPLETED / CANCELLED: terminal visibility
 */
const TABS = [
  { key: "CRITICAL", label: "Action needed" },
  { key: "PICKUP_PASSED", label: "Pickup delayed" },
  { key: "ACTIVE", label: "All active" },
  { key: "UNFULFILLED", label: "Unfulfilled" },
  { key: "COMPLETED", label: "Completed" },
  { key: "CANCELLED", label: "Cancelled" },
];

/** ✅ Filter only for these tabs */
const FILTER_TABS = new Set(["UNFULFILLED", "COMPLETED", "CANCELLED"]);

/** ✅ Range options (matching screenshot style) */
const RANGE_OPTIONS = [
  { key: "ALL", label: "All rides" },
  { key: "TODAY", label: "Today" },
  { key: "LAST_7_DAYS", label: "Last 7 days" },
  { key: "LAST_30_DAYS", label: "Last 30 days" },
  { key: "LAST_12_MONTHS", label: "Last 12 months" },
  { key: "CUSTOM", label: "Custom" },
];

/** ✅ View/page options */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DT_FMT = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function fmtDT(v) {
  const d = parseDate(v);
  if (!d) return "—";
  const s = DT_FMT.format(d);
  return s.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}

function upper(x) {
  return String(x || "").trim().toUpperCase();
}

function fmtMoneyINR(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `₹${n.toLocaleString("en-IN")}`;
}

/**
 * Backend currently uses `breachMins` as "minsToPickup" on list rows.
 * For detail, sometimes it can be in booking.breachMins or booking.minsToPickup etc.
 */
function minsToPickupFromRow(x) {
  const m =
    x?.minsToPickup ??
    x?.pickupMins ??
    x?.breachMins ??
    x?.riskMins ??
    null;
  if (m == null) return null;
  const n = Number(m);
  return Number.isNaN(n) ? null : Math.floor(n);
}

function paymentTone(pay) {
  const s = upper(pay);
  if (!s) return "slate";
  if (s.includes("PAID") || s.includes("SUCCESS") || s.includes("CAPTURE"))
    return "green";
  if (
    s.includes("PEND") ||
    s.includes("INIT") ||
    s.includes("HOLD") ||
    s.includes("CREATED") ||
    s.includes("UNPAID")
  )
    return "amber";
  if (s.includes("FAIL") || s.includes("REFUND") || s.includes("CANCEL"))
    return "red";
  return "slate";
}

function refundTone(status) {
  const s = upper(status);
  if (s === "COMPLETED") return "green";
  if (s === "PENDING" || s === "IN_PROGRESS" || s === "CREATED") return "amber";
  if (s === "FAILED" || s === "NOT_REFUNDED") return "red";
  return "slate";
}

function isScheduledPlanKey(planKey) {
  const s = upper(planKey);
  return !!s && !s.startsWith("RIDE_NOW_");
}

function Pill({ tone = "slate", children, title, className = "" }) {
  const map = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    green: "bg-emerald-100 text-emerald-700 border-emerald-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
    ink: "bg-slate-900 text-white border-slate-900",
  };
  return (
    <span
      title={title}
      className={cls(
        "inline-flex items-center justify-center text-[11px] px-2 py-1 rounded-md border font-semibold leading-none",
        "whitespace-nowrap",
        map[tone] || map.slate,
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * ⚠️ IMPORTANT:
 * - Your list rows have:
 *    - bucket (tab bucket)
 *    - attentionState (ops classification)
 */
function issueBadgeFromRow(x) {
  const state = upper(x?.attentionState);
  const bucket = upper(x?.bucket);

  if (bucket === "CANCELLED") return { label: "Cancelled", tone: "slate" };
  if (bucket === "COMPLETED") return { label: "Completed", tone: "green" };
  if (bucket === "UNFULFILLED") return { label: "Unfulfilled", tone: "amber" };

  if (state === "PICKUP_PASSED_NOT_STARTED")
    return { label: "Pickup delayed", tone: "red" };
  if (state === "UNASSIGNED_NEAR_PICKUP")
    return { label: "Needs ops", tone: "violet" };

  if (state.startsWith("DRIVER_PENDING")) {
    return { label: "Legacy pending", tone: "slate" };
  }

  if (state === "STUCK_UNKNOWN") return { label: "Stuck", tone: "amber" };

  return { label: "—", tone: "slate" };
}

function fmtDurationFromMinutes(mins) {
  const m = Math.max(0, Number(mins || 0));
  const day = 24 * 60;

  if (m >= day) return `${Math.floor(m / day)}d`;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${mm}m`;
  }
  return `${m}m`;
}

function riskBadgeFromMinsToPickup(mins) {
  if (mins == null) return { label: "—", tone: "slate", isBreach: false };

  if (mins < 0) {
    const dur = fmtDurationFromMinutes(Math.abs(mins));
    return { label: `BREACH ${dur}`, tone: "red", isBreach: true };
  }

  if (mins <= 30) {
    return {
      label: `DUE ${fmtDurationFromMinutes(mins)}`,
      tone: "amber",
      isBreach: false,
    };
  }

  return { label: fmtDurationFromMinutes(mins), tone: "slate", isBreach: false };
}

function pickupTextClass(minsToPickup) {
  if (minsToPickup == null) return "text-slate-700";
  if (minsToPickup < 0) return "text-red-700 font-semibold";
  if (minsToPickup <= 30) return "text-amber-700 font-semibold";
  return "text-slate-700";
}

function safeJson(x) {
  try {
    return JSON.stringify(x ?? null, null, 2);
  } catch {
    return String(x);
  }
}

async function copyText(txt) {
  try {
    await navigator.clipboard.writeText(txt);
    return true;
  } catch {
    return false;
  }
}

function RowKV({ k, v }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-2 border-b border-slate-200 last:border-b-0">
      <div className="col-span-4 text-xs text-slate-500">{k}</div>
      <div className="col-span-8 text-sm text-slate-900 text-right break-words">
        {v ?? "—"}
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, children, right }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          {subtitle ? (
            <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-4">{children}</div>
    </div>
  );
}

function backendBucketFor(tabKey) {
  return tabKey;
}

/* ----------------------------- ✅ Range helpers ----------------------------- */
/**
 * Key fix:
 * - Never use `toISOString()` because it adds `Z` (UTC) and can shift window on backend.
 * - Use LOCAL iso without timezone, and use [from, toExclusive) windows.
 */
function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}
function toLocalIsoNoZ(d) {
  if (!isValidDate(d)) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}
function startOfLocalDay(now = new Date()) {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
}
function addDays(d, days) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}
function computeRange(rangeKey, customFrom, customTo) {
  const now = new Date();
  if (!rangeKey || rangeKey === "ALL") return { from: null, toExclusive: null };

  if (rangeKey === "TODAY") {
    const from = startOfLocalDay(now);
    const toExclusive = addDays(from, 1);
    return { from, toExclusive };
  }

  if (rangeKey === "LAST_7_DAYS") {
    const toExclusive = addDays(startOfLocalDay(now), 1);
    const from = addDays(toExclusive, -7);
    return { from, toExclusive };
  }

  if (rangeKey === "LAST_30_DAYS") {
    const toExclusive = addDays(startOfLocalDay(now), 1);
    const from = addDays(toExclusive, -30);
    return { from, toExclusive };
  }

  if (rangeKey === "LAST_12_MONTHS") {
    const toExclusive = addDays(startOfLocalDay(now), 1);
    const from = addDays(toExclusive, -365);
    return { from, toExclusive };
  }

  if (rangeKey === "CUSTOM") {
    const from = customFrom ? new Date(`${customFrom}T00:00:00`) : null;
    const toExclusive = customTo
      ? addDays(new Date(`${customTo}T00:00:00`), 1)
      : null;
    return { from, toExclusive };
  }

  return { from: null, toExclusive: null };
}

/* -------------- ✅ Optional UI guard: filter by pickup time -------------- */
function rowPickupDate(row) {
  const v = row?.pickupAt || row?.pickupTime || row?.pickupDateTime;
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}
function inWindow(d, from, toExclusive) {
  if (!d) return false;
  if (from && d < from) return false;
  if (toExclusive && d >= toExclusive) return false;
  return true;
}

export default function OpsBookings() {
  const [tab, setTab] = useState("CRITICAL");
  const [q, setQ] = useState("");

  const [busy, setBusy] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");

  // ✅ pagination
  const [page, setPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(25);

  // ✅ Range filter (only for UNFULFILLED / COMPLETED / CANCELLED)
  const [rangeKey, setRangeKey] = useState("LAST_30_DAYS");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);

  const [reasons, setReasons] = useState([]);
  const [modal, setModal] = useState(null);

  const [drawerTab, setDrawerTab] = useState("OVERVIEW");
  const [copied, setCopied] = useState("");

  const debounceRef = useRef(null);
  const loadSeqRef = useRef(0);
  const autoTickRef = useRef(null);
  const autoDetailTickRef = useRef(null);

  const [countsByTab, setCountsByTab] = useState(() => {
    const obj = {};
    TABS.forEach((t) => (obj[t.key] = 0));
    return obj;
  });

  const reasonOptions = useMemo(() => {
    const xs = (reasons || []).map((r) => ({ code: r.code, label: r.label }));
    return xs.length
      ? xs
      : [{ code: "SOP_REQUIRED", label: "SOP required action" }];
  }, [reasons]);

  const totalPages = useMemo(() => {
    const t = Number(total || 0);
    const s = Number(pageSize || 1);
    return Math.max(1, Math.ceil(t / s));
  }, [total, pageSize]);

  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  const load = async (queryValue, { silent = false, pageOverride } = {}) => {
    const seq = ++loadSeqRef.current;

    if (!silent) setBusy(true);
    setErr("");

    const query = queryValue ?? q;
    const pageToUse = pageOverride != null ? pageOverride : page;

    try {
      const isRangeEnabled = FILTER_TABS.has(tab);
      const { from, toExclusive } = isRangeEnabled
        ? computeRange(rangeKey, customFrom, customTo)
        : { from: null, toExclusive: null };

      // ✅ list query base
      const base = {
        q: query || "",
        page: pageToUse,
        size: pageSize,
        window: 900,

        // ✅ Only sent for UNFULFILLED/COMPLETED/CANCELLED
        ...(isRangeEnabled && rangeKey
          ? {
              range: rangeKey,
              ...(from && toExclusive
                ? {
                    from: toLocalIsoNoZ(from),
                    to: toLocalIsoNoZ(toExclusive),
                  }
                : {}),
            }
          : {}),
      };

      // Main list (current tab)
      const res = await opsListBookings({
        ...base,
        bucket: backendBucketFor(tab),
      });
      if (seq !== loadSeqRef.current) return;

      const items = Array.isArray(res?.items) ? res.items : [];
      setRows(items);

      const t = Number(res?.total ?? 0);
      setTotal(t);

      // If page is now out of range (e.g. filter reduced results), jump back to last valid page
      const tp = Math.max(1, Math.ceil((t || 0) / (pageSize || 1)));
      if (pageToUse > tp - 1) {
        const newPage = Math.max(0, tp - 1);
        setPage(newPage);

        const rr = await opsListBookings({
          ...base,
          page: newPage,
          bucket: backendBucketFor(tab),
        });
        if (seq !== loadSeqRef.current) return;
        setRows(Array.isArray(rr?.items) ? rr.items : []);
        setTotal(Number(rr?.total ?? t));
      }

      // Counts for tabs (fast: size=1)
      // ✅ Keep counts "global" (do NOT apply range filter to counts)
      const buckets = TABS.map((t2) => t2.key);
      const countBase = {
        q: query || "",
        page: 0,
        size: 1,
        window: 900,
      };
      const results = await Promise.all(
        buckets.map((bk) =>
          opsListBookings({ ...countBase, bucket: backendBucketFor(bk) }).catch(
            () => null
          )
        )
      );
      if (seq !== loadSeqRef.current) return;

      const nextCounts = {};
      buckets.forEach((bk, i) => {
        const r = results[i];
        nextCounts[bk] = Number(r?.total) || Number(r?.items?.length) || 0;
      });
      setCountsByTab((prev) => ({ ...prev, ...nextCounts }));
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setRows([]);
      setTotal(0);
      setErr(e?.message || "Failed to load bookings");
    } finally {
      if (seq !== loadSeqRef.current) return;
      if (!silent) setBusy(false);
    }
  };

  function breachTone(type, open) {
    const t = upper(type);
    if (open) {
      if (t === "PICKUP_PASSED") return "red";
      if (t === "PAYMENT_UNPAID_PICKUP_DUE") return "amber";
      if (t === "UNASSIGNED_NEAR_PICKUP") return "violet";
      return "red";
    }
    return "slate";
  }

  function fmtWhen(v) {
    if (!v) return "—";
    return fmtDT(v);
  }

  const open = async (id, { silent = false } = {}) => {
    setSelectedId(id);
    setErr("");

    try {
      const d = await opsGetBookingDetail(id);
      setDetail(d);

      const a = await opsAuditLogs({
        entityType: "Booking",
        entityId: String(id),
        page: 0,
        size: 50,
      });
      setAudit(a?.content || []);
    } catch (e) {
      if (!silent) setErr(e?.message || "Failed to load booking detail");
      setDetail(null);
      setAudit([]);
    }
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setDetail(null);
    setAudit([]);
    setModal(null);
    setDrawerTab("OVERVIEW");
    setCopied("");
    setErr("");
  };

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const rr = await opsGetReasons();
        setReasons(rr?.reasons || []);
      } catch {}
      await load("", { silent: false, pageOverride: 0 });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload on tab change (reset page)
  useEffect(() => {
    setPage(0);
    load(q, { silent: false, pageOverride: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ✅ Reload when filter changes (only on the 3 tabs)
  useEffect(() => {
    if (!FILTER_TABS.has(tab)) return;

    // For custom: wait until both dates selected
    if (rangeKey === "CUSTOM" && (!customFrom || !customTo)) return;

    setPage(0);
    load(q, { silent: false, pageOverride: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, customFrom, customTo, tab]);

  // Search debounce (reset page)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      load(q, { silent: true, pageOverride: 0 });
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Page/pageSize change
  useEffect(() => {
    load(q, { silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Auto refresh list (keeps page)
  useEffect(() => {
    if (autoTickRef.current) clearInterval(autoTickRef.current);
    autoTickRef.current = setInterval(() => {
      load(q, { silent: true });
    }, 12000);
    return () => {
      if (autoTickRef.current) clearInterval(autoTickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, page, pageSize, rangeKey, customFrom, customTo]);

  // Auto refresh detail
  useEffect(() => {
    if (autoDetailTickRef.current) clearInterval(autoDetailTickRef.current);

    if (selectedId) {
      autoDetailTickRef.current = setInterval(() => {
        open(selectedId, { silent: true });
      }, 15000);
    }

    return () => {
      if (autoDetailTickRef.current) clearInterval(autoDetailTickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Optional sorting + ✅ optional UI guard filtering for the 3 tabs
  const viewRows = useMemo(() => {
    let xs = Array.isArray(rows) ? rows.slice() : [];

    if (tab === "CANCELLED") {
      xs = xs.filter((r) => isScheduledPlanKey(r?.planKey));
    }

    // ✅ UI guard: if backend ignores/loosely applies range, enforce pickup-time window on UI
    if (FILTER_TABS.has(tab) && rangeKey && rangeKey !== "ALL") {
      const { from, toExclusive } = computeRange(rangeKey, customFrom, customTo);
      if (rangeKey !== "CUSTOM" || (customFrom && customTo)) {
        xs = xs.filter((r) => inWindow(rowPickupDate(r), from, toExclusive));
      }
    }

    if (tab === "CRITICAL") {
      xs.sort((a, b) => {
        const as = Number(a?.attentionScore || 0);
        const bs = Number(b?.attentionScore || 0);
        if (bs !== as) return bs - as;

        const am = minsToPickupFromRow(a);
        const bm = minsToPickupFromRow(b);
        if (am == null && bm == null) return 0;
        if (am == null) return 1;
        if (bm == null) return -1;
        return am - bm;
      });
    } else if (tab === "PICKUP_PASSED") {
      xs.sort((a, b) => {
        const am = minsToPickupFromRow(a);
        const bm = minsToPickupFromRow(b);
        if (am == null && bm == null) return 0;
        if (am == null) return 1;
        if (bm == null) return -1;
        return am - bm;
      });
    } else {
      xs.sort(
        (a, b) =>
          Number(b?.bookingId || b?.id || 0) - Number(a?.bookingId || a?.id || 0)
      );
    }
    return xs;
  }, [rows, tab, rangeKey, customFrom, customTo]);

  const headerHint = useMemo(() => {
    if (busy) return "Loading…";
    const t = Number(total || 0);
    const from = t === 0 ? 0 : page * pageSize + 1;
    const to = Math.min(t, (page + 1) * pageSize);
    return `Showing ${from}-${to} of ${t}`;
  }, [busy, total, page, pageSize]);

  const tabMeta = useMemo(() => {
    return TABS.find((t) => t.key === tab) || TABS[0];
  }, [tab]);

  const overviewLine = useMemo(() => {
    return `${tabMeta.label} • ${headerHint} • Auto-refresh 12s`;
  }, [tabMeta.label, headerHint]);

  const b = detail?.booking || null;
  const notes = detail?.notes || [];
  const timeline = detail?.timeline || [];
  const related = detail?.related || null;
  const breaches = detail?.breaches || [];

  const drawerMins = minsToPickupFromRow(b);
  const drawerRisk = riskBadgeFromMinsToPickup(drawerMins);

  const drawerSubtitle = useMemo(() => {
    if (!b) return "";
    const pickup = fmtDT(b.pickupAt || b.pickupTime || b.pickupDateTime);
    const pay = b.paymentStatus || "—";
    return `Pickup: ${pickup} • Payment: ${pay} • ${drawerRisk.label}`;
  }, [b, drawerRisk.label]);

  const fullBundleJson = useMemo(() => safeJson(detail), [detail]);
  const bookingJson = useMemo(() => safeJson(detail?.booking), [detail]);

  return (
    <div className="space-y-4">
      {/* Header (sticky, search on top-right, tabs below) */}
      <div className="sticky top-3 z-30">
        <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="p-4 space-y-3">
            {/* Title row */}
            <div className="flex items-start gap-3 overflow-x-auto whitespace-nowrap flex-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="shrink-0 min-w-[260px]">
                <div className="flex items-center gap-2">
                  <div className="text-xl font-semibold tracking-tight text-slate-900">
                    Bookings
                  </div>

                  <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Live
                  </span>
                </div>

                <div className="mt-1 text-sm text-slate-600">
                  {overviewLine}
                  <span className="ml-2 text-slate-500">
                    {tab === "CANCELLED"
                      ? "Showing cancelled scheduled-trip bookings with refund and transaction traceability."
                      : "Accepted bookings only. Driver acceptance already happened before a booking exists."}
                  </span>
                </div>

                {err ? (
                  <div className="mt-2 text-sm text-red-600 whitespace-normal">
                    {err}
                  </div>
                ) : null}
              </div>

              <div className="ml-auto shrink-0 flex items-center gap-2">
                <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 w-[560px] max-w-[60vw] min-w-[340px]">
                  <span className="text-slate-400 text-sm">⌕</span>
                  <input
                    className="w-full bg-transparent text-sm outline-none"
                    placeholder="Search bookingId / phone / email / planKey / route"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        clearTimeout(debounceRef.current);
                        setPage(0);
                        load(e.currentTarget.value, {
                          silent: false,
                          pageOverride: 0,
                        });
                      }
                    }}
                  />
                  {q ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-semibold hover:bg-slate-50"
                      onClick={() => {
                        setQ("");
                        setPage(0);
                        load("", { silent: false, pageOverride: 0 });
                      }}
                      title="Clear"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                <button
                  className="h-10 rounded-md border border-slate-200 px-4 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
                  disabled={busy}
                  onClick={() => load(q, { silent: false })}
                  type="button"
                  title="Refresh"
                >
                  {busy ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>

            {/* Tabs row */}
            <div className="w-full space-y-2">
              <div
                className={cls(
                  "w-full flex items-center gap-2",
                  "md:overflow-visible md:flex-nowrap",
                  "overflow-x-auto whitespace-nowrap flex-nowrap",
                  "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                )}
              >
                {TABS.map((x) => {
                  const isActive = tab === x.key;

                  return (
                    <button
                      key={x.key}
                      type="button"
                      onClick={() => setTab(x.key)}
                      className={cls(
                        "h-10 rounded-md border text-sm font-semibold inline-flex items-center justify-center gap-2",
                        "shrink-0 md:shrink md:min-w-0",
                        "md:flex-1",
                        "min-w-[160px] px-4",
                        isActive
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white border-slate-200 hover:bg-slate-50"
                      )}
                    >
                      <span className="whitespace-nowrap">{x.label}</span>
                      <span
                        className={cls(
                          "text-[11px] px-2 py-[3px] rounded-md border",
                          isActive
                            ? "border-white/20 bg-white/10 text-white"
                            : "border-slate-200 bg-slate-50 text-slate-700"
                        )}
                      >
                        {countsByTab[x.key] ?? 0}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* ✅ Filter row (only UNFULFILLED / COMPLETED / CANCELLED) */}
              {FILTER_TABS.has(tab) ? (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="text-xs text-slate-500">
                    Filter by time range
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800"
                      value={rangeKey}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRangeKey(v);
                        setPage(0);
                        if (v !== "CUSTOM") {
                          setCustomFrom("");
                          setCustomTo("");
                        }
                      }}
                    >
                      {RANGE_OPTIONS.map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </select>

                    {rangeKey === "CUSTOM" ? (
                      <>
                        <div className="text-xs text-slate-500">From</div>
                        <input
                          type="date"
                          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                          value={customFrom}
                          onChange={(e) => setCustomFrom(e.target.value)}
                        />
                        <div className="text-xs text-slate-500">To</div>
                        <input
                          type="date"
                          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                          value={customTo}
                          onChange={(e) => setCustomTo(e.target.value)}
                        />
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-auto min-w-[1100px]">
            <colgroup>
              {tab === "CANCELLED" ? (
                <>
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "10%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "10%" }} />
                </>
              )}
            </colgroup>

            <thead className="bg-slate-50">
              <tr className="text-xs font-semibold tracking-wider text-slate-500">
                {tab === "CANCELLED" ? (
                  <>
                    <th className="text-left px-5 py-3">Booking / User</th>
                    <th className="text-left px-5 py-3">Cancelled by</th>
                    <th className="text-left px-5 py-3">Cancel before pickup</th>
                    <th className="text-left px-5 py-3">Refundability</th>
                    <th className="text-left px-5 py-3">Paid</th>
                    <th className="text-left px-5 py-3">Refunded</th>
                    <th className="text-left px-5 py-3">Refund status</th>
                    <th className="text-left px-5 py-3">User payment txn</th>
                    <th className="text-left px-5 py-3">Refund txn</th>
                  </>
                ) : (
                  <>
                    <th className="text-left px-5 py-3">Booking</th>
                    <th className="text-left px-5 py-3">Pickup</th>
                    <th className="text-left px-5 py-3">Lifecycle</th>
                    <th className="text-left px-5 py-3">User phone</th>
                    <th className="text-left px-5 py-3">Payment</th>
                    <th className="text-left px-5 py-3">Ops status</th>
                    <th className="text-left px-5 py-3">Driver phone</th>
                    <th className="text-left px-5 py-3">Owner phone</th>
                    <th className="text-right px-5 py-3">Risk</th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {busy ? (
                <tr>
                  <td colSpan={9} className="px-5 py-4 text-sm text-slate-600">
                    Loading…
                  </td>
                </tr>
              ) : viewRows.length ? (
                viewRows.map((x, idx) => {
                  const id = x.bookingId ?? x.id;
                  const pickup = fmtDT(
                    x.pickupAt || x.pickupTime || x.pickupDateTime
                  );
                  const status = x.status || "—";
                  const userPhone = x.userPhone || "—";
                  const pay = x.paymentStatus || "—";
                  const driverPhone = x.driverPhone || "—";
                  const ownerPhone = x.ownerPhone || "—";
                  const cancelBy = x.cancelledBy || "—";
                  const refundStatus =
                    x.refundDisplayStatus || x.razorpayRefundStatus || "—";

                  const minsToPickup = minsToPickupFromRow(x);
                  const risk = riskBadgeFromMinsToPickup(minsToPickup);
                  const issue = issueBadgeFromRow(x);

                  const rowKey = id != null ? `b-${id}` : `row-${idx}`;

                  return (
                    <tr
                      key={rowKey}
                      className={cls(
                        "border-t border-slate-200 text-sm cursor-pointer",
                        selectedId === id ? "bg-slate-50" : "hover:bg-slate-50"
                      )}
                      onClick={() => {
                        if (id == null) return;
                        setDrawerTab("OVERVIEW");
                        setCopied("");
                        open(id);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && id != null && open(id)
                      }
                    >
                      <td className="px-5 py-3 text-slate-900">
                        <div className="font-semibold">{id != null ? `#${id}` : "—"}</div>
                        {tab === "CANCELLED" ? (
                          <div className="mt-1 text-xs text-slate-500">
                            <a
                              href={`/admin/support/users/${encodeURIComponent(String(x.userId || ""))}`}
                              onClick={(e) => e.stopPropagation()}
                              className={cls(
                                "font-semibold",
                                x.userId
                                  ? "text-sky-700 hover:underline"
                                  : "text-slate-500 pointer-events-none"
                              )}
                            >
                              {x.userId ? `User #${x.userId}` : "User —"}
                            </a>
                          </div>
                        ) : null}
                        {tab === "CANCELLED" ? (
                          <div className="mt-1 text-[11px] text-slate-500 truncate" title={x.planKey || ""}>
                            {x.planKey || "—"}
                          </div>
                        ) : null}
                      </td>

                      <td
                        className={cls(
                          "px-5 py-3",
                          tab === "CANCELLED" ? "text-slate-700" : pickupTextClass(minsToPickup)
                        )}
                      >
                        {tab === "CANCELLED" ? (
                          <>
                            <div className="font-medium text-slate-900">{cancelBy}</div>
                            <div className="mt-1 text-[11px] text-slate-500">
                              {fmtDT(x.cancelledAt)}
                            </div>
                          </>
                        ) : (
                          pickup
                        )}
                      </td>

                      <td className="px-5 py-3 text-slate-700">
                        {tab === "CANCELLED" ? (
                          <>
                            <div className="font-medium text-slate-900">
                              {x.cancelBeforePickupLabel || "—"}
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500">{pickup}</div>
                            {x.cancelReason ? (
                              <div className="mt-1 text-[11px] text-slate-500 truncate" title={x.cancelReason}>
                                {x.cancelReason}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <div className="font-medium text-slate-900">{status}</div>
                            {tab === "COMPLETED" ? <div className="mt-1 text-[11px] text-slate-500">Ride finished successfully</div> : null}
                            {tab === "CANCELLED" && x.cancelReason ? <div className="mt-1 text-[11px] text-slate-500 truncate" title={x.cancelReason}>{x.cancelReason}</div> : null}
                          </>
                        )}
                      </td>

                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">
                        {tab === "CANCELLED" ? (
                          <Pill tone={x.refundEligible ? "green" : "red"}>
                            {x.refundEligibilityLabel || "—"}
                          </Pill>
                        ) : (
                          userPhone
                        )}
                      </td>

                      <td className="px-5 py-3">
                        {tab === "CANCELLED" ? (
                          <span className="font-semibold text-slate-900">{fmtMoneyINR(x.paidAmount)}</span>
                        ) : (
                          <Pill tone={paymentTone(pay)}>{pay}</Pill>
                        )}
                      </td>

                      <td className="px-5 py-3">
                        {tab === "CANCELLED" ? (
                          <span className="font-semibold text-slate-900">{fmtMoneyINR(x.refundedAmount)}</span>
                        ) : issue.label === "—" ? (
                          <span className="text-slate-500 text-sm">—</span>
                        ) : (
                          <Pill tone={issue.tone}>{issue.label}</Pill>
                        )}
                      </td>

                      <td className="px-5 py-3 text-slate-700">
                        {tab === "CANCELLED" ? (
                          <>
                            <Pill tone={refundTone(refundStatus)}>{refundStatus}</Pill>
                            <div className="mt-1 text-[11px] text-slate-500">
                              Pending {fmtMoneyINR(x.pendingRefundAmount)}
                            </div>
                          </>
                        ) : (
                          driverPhone
                        )}
                      </td>

                      <td className="px-5 py-3 text-slate-700">
                        {tab === "CANCELLED" ? (
                          <>
                            <div className="font-medium text-slate-900 break-all">
                              {x.razorpayPaymentId || "—"}
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500">
                              Payment #{x.paymentId || "—"}
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500 break-all">
                              {x.razorpayOrderId || "Order —"}
                            </div>
                          </>
                        ) : (
                          ownerPhone
                        )}
                      </td>

                      <td className="px-5 py-3 text-right">
                        {tab === "CANCELLED" ? (
                          <div className="text-left">
                            <div className="font-medium text-slate-900 break-all">
                              {x.razorpayRefundId || "—"}
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500">
                              {x.razorpayRefundStatus || "—"}
                            </div>
                          </div>
                        ) : risk.label === "—" ? (
                          <span className="text-slate-500 text-sm">—</span>
                        ) : (
                          <Pill tone={risk.tone}>{risk.label}</Pill>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="px-5 py-4 text-sm text-slate-600">
                    No bookings found for this tab.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-xs text-slate-500">Live updates every 12s</div>

            <div className="flex items-center justify-end gap-4 flex-wrap">
              {/* ✅ View/page (fix double arrow) */}
              <div className="flex items-center gap-2">
                <div className="text-sm text-slate-700 whitespace-nowrap">
                  View
                </div>
                <div className="relative">
                  <select
                    className="h-10 rounded-md border border-slate-200 bg-white pl-3 pr-10 text-sm font-semibold text-slate-900 appearance-none"
                    value={pageSize}
                    onChange={(e) => {
                      const n = Number(e.target.value || 25);
                      setPage(0);
                      setPageSize(Number.isNaN(n) ? 25 : n);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}/page
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                    ▼
                  </div>
                </div>
              </div>

              <div className="text-sm text-slate-700 whitespace-nowrap">
                Page <span className="font-semibold">{page + 1}</span> of{" "}
                <span className="font-semibold">{totalPages}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  disabled={busy || !canPrev}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>

                <button
                  type="button"
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  disabled={busy || !canNext}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <OpsRightDrawer
        open={!!selectedId}
        title={selectedId ? `Booking #${selectedId}` : "Booking"}
        subtitle={drawerSubtitle}
        onClose={closeDrawer}
        width={980}
      >
        <div className="p-5 space-y-4">
          {!b ? (
            <div className="text-sm text-slate-600">No details loaded.</div>
          ) : (
            <>
              {/* Drawer header */}
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Booking
                    </div>
                    <div className="text-xs text-slate-500">
                      Read-only • internal notes allowed • audited
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
                      disabled={!selectedId}
                      onClick={() => selectedId && open(selectedId)}
                      type="button"
                    >
                      Refresh
                    </button>

                    <button
                      className="h-9 px-3 rounded-md bg-slate-900 text-white text-sm font-semibold"
                      onClick={() =>
                        setModal({ type: "note", note: "", tags: [] })
                      }
                      type="button"
                    >
                      Add note
                    </button>
                  </div>
                </div>

                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDrawerTab("OVERVIEW")}
                      className={cls(
                        "h-9 px-3 rounded-md border text-sm font-semibold",
                        drawerTab === "OVERVIEW"
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white border-slate-200 hover:bg-slate-50"
                      )}
                    >
                      Overview
                    </button>

                    <button
                      type="button"
                      onClick={() => setDrawerTab("RAW")}
                      className={cls(
                        "h-9 px-3 rounded-md border text-sm font-semibold",
                        drawerTab === "RAW"
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white border-slate-200 hover:bg-slate-50"
                      )}
                    >
                      Raw JSON
                    </button>
                  </div>

                  {drawerTab === "RAW" ? (
                    <div className="flex items-center gap-2">
                      {copied ? (
                        <div className="text-xs text-emerald-700 font-semibold">
                          Copied {copied}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                        onClick={async () => {
                          const ok = await copyText(bookingJson);
                          setCopied(ok ? "booking JSON" : "");
                          setTimeout(() => setCopied(""), 1200);
                        }}
                      >
                        Copy booking
                      </button>
                      <button
                        type="button"
                        className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                        onClick={async () => {
                          const ok = await copyText(fullBundleJson);
                          setCopied(ok ? "full bundle JSON" : "");
                          setTimeout(() => setCopied(""), 1200);
                        }}
                      >
                        Copy all
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              {drawerTab === "OVERVIEW" ? (
                <>
                  <SectionCard
                    title="Booking details"
                    subtitle="Core information for ops decisions"
                    right={
                      <div className="flex items-center gap-2">
                        <Pill tone={paymentTone(b.paymentStatus)}>
                          {b.paymentStatus || "—"}
                        </Pill>
                        {drawerRisk.label !== "—" ? (
                          <Pill tone={drawerRisk.tone}>{drawerRisk.label}</Pill>
                        ) : null}
                      </div>
                    }
                  >
                    <RowKV
                      k="Booking ID"
                      v={b.bookingId != null ? `#${b.bookingId}` : "—"}
                    />
                    <RowKV k="Status" v={b.status || "—"} />
                    <RowKV k="Plan key" v={b.planKey || "—"} />
                    <RowKV
                      k="Pickup"
                      v={
                        <span className={pickupTextClass(drawerMins)}>
                          {fmtDT(b.pickupAt || b.pickupTime || b.pickupDateTime)}
                        </span>
                      }
                    />
                    <RowKV
                      k="Drop"
                      v={fmtDT(b.dropAt || b.dropTime || b.dropDateTime)}
                    />
                    <RowKV k="Vehicle" v={b.vehicleRegNo || "—"} />
                    <RowKV k="Match batch" v={b.matchBatchNo ?? "—"} />
                    <RowKV k="Self drive" v={b.selfDrive ? "YES" : "NO"} />
                    <RowKV k="Assigned driver ID" v={b.assignedDriverId ?? "—"} />
                    <RowKV
                      k="Issue"
                      v={(() => {
                        const issue = issueBadgeFromRow(b);
                        return issue.label === "—" ? (
                          "—"
                        ) : (
                          <Pill tone={issue.tone}>{issue.label}</Pill>
                        );
                      })()}
                    />
                  </SectionCard>

                  <SectionCard
                    title="Contact details"
                    subtitle="User, driver and owner contacts"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-3">
                      <div className="rounded-lg border border-slate-200 p-3">
                        <div className="text-xs font-semibold text-slate-500">
                          USER
                        </div>
                        <div className="mt-2 space-y-1">
                          <div className="text-sm text-slate-900 font-semibold">
                            {b.userName || "—"}
                          </div>
                          <div className="text-sm text-slate-700">
                            {b.userPhone || "—"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {b.userEmail || "—"}
                          </div>
                          <div className="text-xs text-slate-500">
                            ID: {b.userId ?? "—"}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3">
                        <div className="text-xs font-semibold text-slate-500">
                          DRIVER
                        </div>
                        <div className="mt-2 space-y-1">
                          <div className="text-sm text-slate-900 font-semibold">
                            {b.driverName || "—"}
                          </div>
                          <div className="text-sm text-slate-700">
                            {b.driverPhone || "—"}
                          </div>
                          <div className="text-xs text-slate-500">
                            ID: {b.assignedDriverId ?? "—"}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3">
                        <div className="text-xs font-semibold text-slate-500">
                          OWNER
                        </div>
                        <div className="mt-2 space-y-1">
                          <div className="text-sm text-slate-900 font-semibold">
                            {b.ownerName || "—"}
                          </div>
                          <div className="text-sm text-slate-700">
                            {b.ownerPhone || "—"}
                          </div>
                          <div className="text-xs text-slate-500">
                            ID: {b.ownerId ?? "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard title="Route" subtitle="Pickup, drop and stops">
                    <RowKV k="Pickup location" v={b.pickupLocation || "—"} />
                    <RowKV k="Drop location" v={b.destination || "—"} />
                    <RowKV
                      k="Stops"
                      v={
                        Array.isArray(b.destinations) && b.destinations.length
                          ? b.destinations.join(" → ")
                          : "—"
                      }
                    />
                    <RowKV k="Full route" v={b.route || "—"} />
                  </SectionCard>

                  <SectionCard
                    title="Payment & request summary"
                    subtitle="Payment numbers and linked request info"
                  >
                    <RowKV
                      k="Payment status"
                      v={
                        <Pill tone={paymentTone(b.paymentStatus)}>
                          {b.paymentStatus || "—"}
                        </Pill>
                      }
                    />
                    <RowKV k="Paid amount" v={fmtMoneyINR(b.paidAmount)} />
                    <RowKV
                      k="Refunded amount"
                      v={fmtMoneyINR(b.refundedAmount)}
                    />
                    <RowKV
                      k="Pending refund"
                      v={fmtMoneyINR(b.pendingRefundAmount)}
                    />
                    <RowKV
                      k="Cancel before pickup"
                      v={b.cancelBeforePickupLabel || "—"}
                    />
                    <RowKV
                      k="Refundability"
                      v={
                        <Pill tone={b.refundEligible ? "green" : "red"}>
                          {b.refundEligibilityLabel || "—"}
                        </Pill>
                      }
                    />
                    <RowKV
                      k="Refund status"
                      v={
                        <Pill tone={refundTone(b.refundDisplayStatus || b.razorpayRefundStatus)}>
                          {b.refundDisplayStatus || b.razorpayRefundStatus || "—"}
                        </Pill>
                      }
                    />
                    <RowKV k="Payment row ID" v={b.paymentId ?? "—"} />
                    <RowKV k="Payment created at" v={fmtDT(b.paymentCreatedAt)} />
                    <RowKV k="Payment paid at" v={fmtDT(b.paymentPaidAt)} />
                    <RowKV k="Razorpay order ID" v={b.razorpayOrderId || "—"} />
                    <RowKV k="Razorpay payment ID" v={b.razorpayPaymentId || "—"} />
                    <RowKV k="Razorpay refund ID" v={b.razorpayRefundId || "—"} />
                    <RowKV k="Razorpay refund provider status" v={b.razorpayRefundStatus || "—"} />

                    <div className="mt-2" />

                    <RowKV
                      k="Linked request ID"
                      v={related?.requestId ? `#${related.requestId}` : "—"}
                    />
                    <RowKV k="Request status" v={related?.requestStatus || "—"} />
                    <RowKV
                      k="Request payment"
                      v={related?.paymentStatus || "—"}
                    />
                    <RowKV k="Request batch" v={related?.matchBatchNo ?? "—"} />
                  </SectionCard>

                  <SectionCard title="Notes" subtitle="Internal ops notes">
                    <div className="py-3 space-y-2">
                      {notes.length ? (
                        notes.slice(0, 25).map((n) => (
                          <div
                            key={String(n.id)}
                            className="rounded-lg border border-slate-200 p-3"
                          >
                            <div className="text-xs text-slate-500">
                              {fmtDT(n.createdAt)} • Admin {n.adminId ?? "—"}
                            </div>
                            <div className="mt-1 text-sm text-slate-700">
                              {n.note || "—"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600 py-3">
                          No notes.
                        </div>
                      )}
                    </div>
                  </SectionCard>

                  <SectionCard title="Timeline" subtitle="Booking + ops events">
                    <div className="py-3 space-y-2">
                      {timeline.length ? (
                        timeline.slice(0, 40).map((t, idx) => (
                          <div
                            key={idx}
                            className="rounded-lg border border-slate-200 p-3"
                          >
                            <div className="text-xs text-slate-500">
                              {fmtDT(t.at)} • {t.type || t.eventType || "—"}
                            </div>
                            <div className="mt-1 text-sm text-slate-700">
                              {t.message || t.actorType || "—"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600 py-3">
                          No timeline.
                        </div>
                      )}
                    </div>
                  </SectionCard>

                  <SectionCard title="Audit" subtitle="Admin audit logs">
                    <div className="py-3 space-y-2">
                      {audit.length ? (
                        audit.slice(0, 25).map((a) => (
                          <div
                            key={String(a.id)}
                            className="rounded-lg border border-slate-200 p-3"
                          >
                            <div className="text-xs text-slate-500">
                              {fmtDT(a.createdAt)} • {a.actionKey}
                            </div>
                            <div className="mt-1 text-sm text-slate-700">
                              {a.reason || "—"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600 py-3">
                          No audit logs.
                        </div>
                      )}
                    </div>
                  </SectionCard>
                </>
              ) : (
                <>
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50">
                      <div className="text-sm font-semibold text-slate-900">
                        Booking JSON
                      </div>
                      <div className="text-xs text-slate-500">
                        Raw booking row object
                      </div>
                    </div>
                    <pre className="px-4 py-4 text-[12px] leading-relaxed overflow-auto max-h-[520px] bg-white text-slate-800">
                      {bookingJson}
                    </pre>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50">
                      <div className="text-sm font-semibold text-slate-900">
                        Full bundle
                      </div>
                      <div className="text-xs text-slate-500">
                        booking + notes + timeline + related + summary
                      </div>
                    </div>
                    <pre className="px-4 py-4 text-[12px] leading-relaxed overflow-auto max-h-[520px] bg-white text-slate-800">
                      {fullBundleJson}
                    </pre>
                  </div>
                </>
              )}

              {/* Breaches */}
              <SectionCard
                title="Breaches"
                subtitle="Persisted breach ledger (open/settled) with durations"
                right={
                  <div className="flex items-center gap-2">
                    <Pill tone="slate" title="Total breaches in history">
                      Total {Array.isArray(breaches) ? breaches.length : 0}
                    </Pill>
                    <Pill tone="red" title="Open breaches right now">
                      Open{" "}
                      {(Array.isArray(breaches) ? breaches : []).filter((x) => x?.open)
                        .length}
                    </Pill>
                  </div>
                }
              >
                <div className="py-3 space-y-2">
                  {Array.isArray(breaches) && breaches.length ? (
                    breaches.slice(0, 20).map((x, idx) => {
                      const isOpen = !!x?.open;
                      const type = x?.type || "—";
                      const label = x?.label || type;
                      const dur =
                        x?.durationText ||
                        (x?.durationMins != null
                          ? fmtDurationFromMinutes(x.durationMins)
                          : "—");

                      return (
                        <div
                          key={`${type}-${idx}`}
                          className="rounded-lg border border-slate-200 p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">
                                {label}
                              </div>
                              <div className="mt-0.5 text-xs text-slate-500">
                                Type: {upper(type)}
                              </div>
                            </div>

                            <div className="shrink-0 flex items-center gap-2">
                              <Pill tone={breachTone(type, isOpen)}>
                                {isOpen ? "OPEN" : "SETTLED"}
                              </Pill>
                              <Pill
                                tone={isOpen ? breachTone(type, true) : "slate"}
                                title="Duration"
                              >
                                {dur}
                              </Pill>
                            </div>
                          </div>

                          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-600">
                            <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1">
                              <div className="text-[11px] text-slate-500 font-semibold">
                                Opened
                              </div>
                              <div className="text-slate-800">
                                {fmtWhen(x?.openedAt)}
                              </div>
                            </div>

                            <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1">
                              <div className="text-[11px] text-slate-500 font-semibold">
                                Last seen
                              </div>
                              <div className="text-slate-800">
                                {fmtWhen(x?.lastSeenAt)}
                              </div>
                            </div>

                            <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1">
                              <div className="text-[11px] text-slate-500 font-semibold">
                                Settled
                              </div>
                              <div className="text-slate-800">
                                {fmtWhen(x?.settledAt)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-sm text-slate-600 py-3">
                      No breach history found for this booking.
                    </div>
                  )}

                  {Array.isArray(breaches) && breaches.length > 20 ? (
                    <div className="text-xs text-slate-500">
                      Showing latest 20 breaches. (Open breaches always appear
                      first from backend sorting.)
                    </div>
                  ) : null}
                </div>
              </SectionCard>
            </>
          )}
        </div>
      </OpsRightDrawer>

      {/* Note modal */}
      <OpsActionModal
        open={!!modal}
        title="Add internal note"
        hint="Choose a reason so future admins know why this note was added."
        reasons={reasonOptions}
        confirmLabel="Save note"
        onClose={() => setModal(null)}
        onConfirm={async (reason) => {
          if (!selectedId || !modal) return;
          await opsAddBookingNote(
            selectedId,
            modal.note || "",
            modal.tags || [],
            reason
          );
          await open(selectedId);
          setModal(null);
        }}
      >
        <div>
          <div className="text-sm text-slate-700">Note</div>
          <textarea
            className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[90px]"
            value={modal?.note || ""}
            onChange={(e) => setModal({ ...modal, note: e.target.value })}
            placeholder="What happened + next step…"
          />
        </div>
      </OpsActionModal>
    </div>
  );
}
